// Company → Management → Insurance. Owner, 2026-09-26: "Let's get this
// [the MEWG LLC master insurance spreadsheet] into a disk called Insurance
// under Company and Management … same as we did with Leases — need to make
// sure to have all the proper dates, expirations etc."
//
// OWNER/DIRECTOR only, both reads and writes (unlike Licenses, which a
// manager also reads) — the pane itself is hidden from everyone else on the
// Company page (src/pages/Company.tsx), and canWriteInsurance below is the
// same gate enforced again here, in case this ever renders without that.
//
// One table, one row per POLICY (not per clinic — a policy can cover
// several at once, unlike a lease). Documents are a count chip that expands
// an inline list under the row, oldest first, each with its own
// Edit/Remove — the same closed-by-default expander CompanyLeases uses.
// Below the table: a "Certificates & other documents" panel for documents
// naming no policy at all (a certificate filed to one clinic, or a
// company-level document covering several policies at once — owner
// correction, 2026-09-26, while loading the real set).
import type { JSX } from 'preact';
import { webLink } from '../lib/webLink';
import { useEffect, useState } from 'preact/hooks';
import { EmptyState, FaultState } from './FaultState';
import { FavoriteStar } from './FavoriteStar';
import { useWrite } from './MoneyForms';
import { useResource } from '../lib/store';
import { session } from '../lib/session';
import { clinicToday } from '../lib/clinicDay';
import { LOCATION_ORDER, LOCATION_NAMES } from '../lib/locations';
import { isoToMd } from '../lib/serviceCompliance';
import { extractDriveFileId } from '../lib/licenses';
import {
  INSURANCE_KEY, fetchInsurance, reloadInsurance,
  addInsurancePolicy, updateInsurancePolicy, removeInsurancePolicy, setInsurancePolicyLocations,
  addInsuranceDocument, addInsuranceDocumentForLocation, updateInsuranceDocument, removeInsuranceDocument,
  documentsForPolicy, unattachedDocuments, latestRenewalDocument, policyLocationNames,
  currentPolicies, pastPolicies, policyDaysLeft, INSURANCE_ATTENTION_DAYS, INSURANCE_WARN_DAYS,
  INSURANCE_STATUSES, INSURANCE_STATUS_LABEL,
  INSURANCE_DOCUMENT_KINDS, INSURANCE_DOCUMENT_KIND_LABEL,
  INSURANCE_DOCUMENT_DATE_BASES, INSURANCE_DOCUMENT_DATE_BASIS_LABEL,
  type InsurancePolicyRow, type InsuranceDocumentRow, type InsuranceData, type InsuranceDocumentWrite,
} from '../lib/insurance';

const EM_DASH = '—';
const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function canWriteInsurance(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR'));
}

/** A policy's own page is a real address — `#insurance-<id>` — so a refresh
 * and the Back button land on it, the same as a clinic's lease page
 * (CompanyLeases.tsx's profileClinicFromHash). */
function profilePolicyFromHash(): number | null {
  const m = /^#insurance-(\d+)$/.exec(window.location.hash || '');
  return m ? Number(m[1]) : null;
}

type PolicyFormState = {
  id: number;
  coverageType: string;
  carrier: string;
  policyNumber: string;
  namedInsured: string;
  effectiveDate: string;
  expirationDate: string;
  limits: string;
  deductible: string;
  bpp: string;
  premium: string;
  broker: string;
  brokerContact: string;
  status: InsurancePolicyRow['status'];
  notes: string;
  source: string;
};

function blankPolicyForm(): PolicyFormState {
  return {
    id: 0,
    coverageType: '',
    carrier: '',
    policyNumber: '',
    namedInsured: '',
    effectiveDate: '',
    expirationDate: '',
    limits: '',
    deductible: '',
    bpp: '',
    premium: '',
    broker: '',
    brokerContact: '',
    status: 'ACTIVE',
    notes: '',
    source: '',
  };
}

function policyFormFrom(row: InsurancePolicyRow): PolicyFormState {
  return {
    id: row.id,
    coverageType: row.coverageType,
    carrier: row.carrier ?? '',
    policyNumber: row.policyNumber ?? '',
    namedInsured: row.namedInsured ?? '',
    effectiveDate: row.effectiveDate ?? '',
    expirationDate: row.expirationDate ?? '',
    limits: row.limits ?? '',
    deductible: row.deductible ?? '',
    bpp: row.bpp ?? '',
    premium: row.premium != null ? String(row.premium) : '',
    broker: row.broker ?? '',
    brokerContact: row.brokerContact ?? '',
    status: row.status,
    notes: row.notes ?? '',
    source: row.source ?? '',
  };
}

type DocFormState = {
  id: number;
  kind: string;
  title: string;
  docDate: string;
  dateBasis: string;
  linkInput: string;
  // Only read for the unattached panel's Add form — '' means company-wide.
  locationCode: string;
};

function blankDocForm(): DocFormState {
  return { id: 0, kind: '', title: '', docDate: '', dateBasis: '', linkInput: '', locationCode: '' };
}

function docFormFrom(row: InsuranceDocumentRow): DocFormState {
  return {
    id: row.id,
    kind: row.kind,
    title: row.title,
    docDate: row.docDate ?? '',
    dateBasis: row.dateBasis ?? '',
    linkInput: row.url ?? row.driveUrl ?? '',
    locationCode: row.locationCode ?? '',
  };
}

type LocationsFormState = {
  policyId: number;
  rows: Record<string, { checked: boolean; payrollBasis: string; note: string }>;
};

function blankLocationsForm(policy: InsurancePolicyRow): LocationsFormState {
  const rows: LocationsFormState['rows'] = {};
  for (const code of LOCATION_ORDER) {
    const existing = policy.locations.find((l) => l.locationCode === code);
    rows[code] = {
      checked: Boolean(existing),
      payrollBasis: existing?.payrollBasis ?? '',
      note: existing?.note ?? '',
    };
  }
  return { policyId: policy.id, rows };
}

/** Wash class for a row/cell counting down to expiration — the same two
 * Licenses selectors CompanyLeases already reuses (owner, 2026-09-26: "it
 * should also get highlighted"), here at 60/30 days rather than 12/6
 * months. `null` when there is nothing to measure, or the row is past
 * status (never washed — Tomer, 2026-09-26). */
function expiryWash(daysLeft: number | null): string | undefined {
  if (daysLeft === null || daysLeft < 0) return undefined;
  if (daysLeft <= INSURANCE_WARN_DAYS) return 'license-expired';
  if (daysLeft <= INSURANCE_ATTENTION_DAYS) return 'license-due';
  return undefined;
}

export function CompanyInsurance({ pinKey }: { pinKey?: string }): JSX.Element {
  const data = useResource<InsuranceData>(INSURANCE_KEY, fetchInsurance);
  const save = useWrite();
  const canWrite = canWriteInsurance();
  const today = clinicToday();

  const [policyForm, setPolicyForm] = useState<PolicyFormState | null>(null);
  const [confirmingPolicyId, setConfirmingPolicyId] = useState<number | null>(null);
  const [locationsForm, setLocationsForm] = useState<LocationsFormState | null>(null);
  const [openDocs, setOpenDocs] = useState<Record<string, boolean>>({});
  const [docPanel, setDocPanel] = useState<{ key: string; policyId: number | null; form: DocFormState } | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [profilePolicyId, setProfilePolicyId] = useState<number | null>(() => profilePolicyFromHash());
  useEffect(() => {
    const onHash = (): void => setProfilePolicyId(profilePolicyFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (data.status === 'loading') return <EmptyState label="Loading insurance…" />;
  if (data.status === 'fault') return <FaultState name="insurance_load_failed" detail={data.error} />;

  const policies = data.data.policies ?? [];
  const documents = data.data.documents ?? [];
  const current = currentPolicies(policies);
  const past = pastPolicies(policies);
  const unattached = unattachedDocuments(documents);

  function openPolicyForm(form: PolicyFormState): void {
    setDocPanel(null);
    setConfirmingPolicyId(null);
    setLocationsForm(null);
    setPolicyForm(form);
  }

  function openDocForm(key: string, policyId: number | null, form: DocFormState): void {
    setPolicyForm(null);
    setLocationsForm(null);
    setOpenDocs((prev) => ({ ...prev, [key]: true }));
    setDocPanel({ key, policyId, form });
  }

  function openProfile(id: number | null): void {
    setProfilePolicyId(id);
    window.location.hash = id ? `insurance-${id}` : 'insurance';
  }

  async function submitPolicy(): Promise<void> {
    if (!policyForm) return;
    await save.run(async () => {
      const body = {
        coverageType: policyForm.coverageType.trim(),
        carrier: policyForm.carrier.trim(),
        policyNumber: policyForm.policyNumber.trim(),
        namedInsured: policyForm.namedInsured.trim(),
        effectiveDate: policyForm.effectiveDate,
        expirationDate: policyForm.expirationDate,
        limits: policyForm.limits.trim(),
        deductible: policyForm.deductible.trim(),
        bpp: policyForm.bpp.trim(),
        premium: policyForm.premium.trim(),
        broker: policyForm.broker.trim(),
        brokerContact: policyForm.brokerContact.trim(),
        status: policyForm.status,
        notes: policyForm.notes.trim(),
        source: policyForm.source.trim(),
      };
      if (policyForm.id) await updateInsurancePolicy(policyForm.id, body);
      else await addInsurancePolicy(body);
      setPolicyForm(null);
      await reloadInsurance();
    });
  }

  async function removePolicyConfirmed(id: number): Promise<void> {
    await save.run(async () => {
      await removeInsurancePolicy(id);
      setConfirmingPolicyId(null);
      if (policyForm?.id === id) setPolicyForm(null);
      await reloadInsurance();
    });
  }

  async function submitLocations(): Promise<void> {
    if (!locationsForm) return;
    await save.run(async () => {
      const locations = LOCATION_ORDER
        .filter((code) => locationsForm.rows[code].checked)
        .map((code) => ({
          locationCode: code,
          payrollBasis: locationsForm.rows[code].payrollBasis.trim(),
          note: locationsForm.rows[code].note.trim(),
        }));
      await setInsurancePolicyLocations(locationsForm.policyId, locations);
      setLocationsForm(null);
      await reloadInsurance();
    });
  }

  async function submitDoc(): Promise<void> {
    if (!docPanel) return;
    await save.run(async () => {
      const { form, policyId } = docPanel;
      const pasted = form.linkInput.trim();
      const driveFileId = extractDriveFileId(pasted);
      const body: InsuranceDocumentWrite = {
        kind: form.kind as InsuranceDocumentWrite['kind'],
        title: form.title.trim(),
        docDate: form.docDate,
        dateBasis: form.dateBasis as InsuranceDocumentWrite['dateBasis'],
        driveFileId: driveFileId ?? '',
        // A recognisable Drive file link resolves to driveFileId above; any
        // other link is kept verbatim in `url` instead of being discarded
        // for not being a Drive link — same as CompanyLeases's own submitDoc.
        url: driveFileId ? '' : pasted,
        sourcePath: '',
        sha256: '',
        note: '',
      };
      if (form.id) await updateInsuranceDocument(form.id, body);
      else if (policyId) await addInsuranceDocument(policyId, body);
      else await addInsuranceDocumentForLocation(form.locationCode, body);
      setDocPanel(null);
      await reloadInsurance();
    });
  }

  async function removeDoc(id: number): Promise<void> {
    await save.run(async () => {
      await removeInsuranceDocument(id);
      await reloadInsurance();
    });
  }

  /** The filed paperwork for one policy (or the unattached set), oldest
   * first, with Edit / Remove and the Add document form — the same panel
   * under a table row, on a policy's own page, and in the "Certificates &
   * other documents" panel. `policyId` null means "no single policy" —
   * the Add form then also offers a Clinic picker (company-wide or one of
   * the four), per the unattached document's own two shapes. */
  function renderDocuments(docs: InsuranceDocumentRow[], key: string, policyId: number | null): JSX.Element {
    const showClinicField = policyId === null;
    return (
      <>
        {docs.length === 0 ? (
          <p class="empty">No documents filed yet.</p>
        ) : (
          <ul class="insurance-document-list">
            {docs.map((doc) => {
              const href = webLink(doc.driveUrl ?? doc.url);
              const clinicLabel = doc.locationCode
                ? LOCATION_NAMES[doc.locationCode as keyof typeof LOCATION_NAMES] ?? doc.locationCode
                : null;
              return (
                <li key={doc.id} data-testid={`insurance-document-${doc.id}`}>
                  <span>{isoToMd(doc.docDate)}</span>
                  <span>{INSURANCE_DOCUMENT_KIND_LABEL[doc.kind]}</span>
                  {href ? (
                    <a class="doc-link" href={href} target="_blank" rel="noreferrer">{doc.title}</a>
                  ) : (
                    <span>{doc.title}</span>
                  )}
                  <span class="insurance-doc-note">
                    {clinicLabel ? `${clinicLabel}${doc.note ? ` — ${doc.note}` : ''}` : (doc.note || (showClinicField ? 'Company-wide' : EM_DASH))}
                  </span>
                  {canWrite ? (
                    <span class="insurance-doc-actions">
                      <button
                        type="button"
                        class="btn"
                        disabled={save.busy}
                        onClick={() => openDocForm(key, policyId, docFormFrom(doc))}
                      >
                        Edit
                      </button>
                      <button type="button" class="btn" disabled={save.busy} onClick={() => void removeDoc(doc.id)}>
                        Remove
                      </button>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {canWrite ? (
          docPanel?.key === key ? (
            <form
              class="service-point-form"
              data-testid="insurance-document-form"
              onSubmit={(e) => { e.preventDefault(); void submitDoc(); }}
            >
              {showClinicField ? (
                <label>
                  Clinic
                  <select
                    aria-label="Clinic"
                    data-testid="insurance-document-field-clinic"
                    value={docPanel.form.locationCode}
                    onChange={(e) => setDocPanel({ key, policyId, form: { ...docPanel.form, locationCode: (e.target as HTMLSelectElement).value } })}
                  >
                    <option value="">Company-wide (no single clinic)</option>
                    {LOCATION_ORDER.map((code) => (
                      <option key={code} value={code}>{LOCATION_NAMES[code]}</option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label>
                Kind
                <select
                  aria-label="Document kind"
                  data-testid="insurance-document-field-kind"
                  value={docPanel.form.kind}
                  onChange={(e) => setDocPanel({ key, policyId, form: { ...docPanel.form, kind: (e.target as HTMLSelectElement).value } })}
                >
                  <option value="">— choose —</option>
                  {INSURANCE_DOCUMENT_KINDS.map((k) => (
                    <option key={k} value={k}>{INSURANCE_DOCUMENT_KIND_LABEL[k]}</option>
                  ))}
                </select>
              </label>
              <label>
                Title
                <input
                  data-testid="insurance-document-field-title"
                  value={docPanel.form.title}
                  onInput={(e) => setDocPanel({ key, policyId, form: { ...docPanel.form, title: (e.target as HTMLInputElement).value } })}
                />
              </label>
              <label>
                Document date
                <input
                  type="date"
                  data-testid="insurance-document-field-date"
                  value={docPanel.form.docDate}
                  onInput={(e) => setDocPanel({ key, policyId, form: { ...docPanel.form, docDate: (e.target as HTMLInputElement).value } })}
                />
              </label>
              <label>
                Date basis
                <select
                  aria-label="Date basis"
                  data-testid="insurance-document-field-basis"
                  value={docPanel.form.dateBasis}
                  onChange={(e) => setDocPanel({ key, policyId, form: { ...docPanel.form, dateBasis: (e.target as HTMLSelectElement).value } })}
                >
                  <option value="">— none —</option>
                  {INSURANCE_DOCUMENT_DATE_BASES.map((b) => (
                    <option key={b} value={b}>{INSURANCE_DOCUMENT_DATE_BASIS_LABEL[b]}</option>
                  ))}
                </select>
              </label>
              <label>
                Drive link
                <input
                  data-testid="insurance-document-field-link"
                  placeholder="https://drive.google.com/file/d/… (or any other link the file lives at)"
                  value={docPanel.form.linkInput}
                  onInput={(e) => setDocPanel({ key, policyId, form: { ...docPanel.form, linkInput: (e.target as HTMLInputElement).value } })}
                />
              </label>
              <div class="service-point-actions">
                <button
                  type="submit"
                  class="btn primary"
                  disabled={save.busy || !docPanel.form.kind || !docPanel.form.title.trim()}
                >
                  {docPanel.form.id ? 'Save' : 'Add'}
                </button>
                <button type="button" class="btn" disabled={save.busy} onClick={() => setDocPanel(null)}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              class="btn"
              data-testid={`insurance-add-document-${key}`}
              onClick={() => openDocForm(key, policyId, blankDocForm())}
            >
              Add document
            </button>
          )
        ) : null}
      </>
    );
  }

  function renderPolicyForm(): JSX.Element {
    if (!policyForm) throw new Error('renderPolicyForm called with no form open');
    return (
      <form
        class="service-point-form"
        data-testid="insurance-form"
        onSubmit={(e) => { e.preventDefault(); void submitPolicy(); }}
      >
        <label>
          Coverage type
          <input
            data-testid="insurance-field-coverage-type"
            value={policyForm.coverageType}
            onInput={(e) => setPolicyForm({ ...policyForm, coverageType: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Carrier
          <input
            data-testid="insurance-field-carrier"
            value={policyForm.carrier}
            onInput={(e) => setPolicyForm({ ...policyForm, carrier: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Policy #
          <input
            data-testid="insurance-field-policy-number"
            value={policyForm.policyNumber}
            onInput={(e) => setPolicyForm({ ...policyForm, policyNumber: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Named insured
          <input
            data-testid="insurance-field-named-insured"
            value={policyForm.namedInsured}
            onInput={(e) => setPolicyForm({ ...policyForm, namedInsured: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Effective date
          <input
            type="date"
            data-testid="insurance-field-effective-date"
            value={policyForm.effectiveDate}
            onInput={(e) => setPolicyForm({ ...policyForm, effectiveDate: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Expiration date
          <input
            type="date"
            data-testid="insurance-field-expiration-date"
            value={policyForm.expirationDate}
            onInput={(e) => setPolicyForm({ ...policyForm, expirationDate: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Limits
          <input
            data-testid="insurance-field-limits"
            value={policyForm.limits}
            onInput={(e) => setPolicyForm({ ...policyForm, limits: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Deductible
          <input
            data-testid="insurance-field-deductible"
            value={policyForm.deductible}
            onInput={(e) => setPolicyForm({ ...policyForm, deductible: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          BPP
          <input
            data-testid="insurance-field-bpp"
            value={policyForm.bpp}
            onInput={(e) => setPolicyForm({ ...policyForm, bpp: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Premium
          <input
            data-testid="insurance-field-premium"
            value={policyForm.premium}
            onInput={(e) => setPolicyForm({ ...policyForm, premium: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Broker
          <input
            data-testid="insurance-field-broker"
            value={policyForm.broker}
            onInput={(e) => setPolicyForm({ ...policyForm, broker: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Broker contact
          <input
            data-testid="insurance-field-broker-contact"
            value={policyForm.brokerContact}
            onInput={(e) => setPolicyForm({ ...policyForm, brokerContact: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Status
          <select
            aria-label="Status"
            data-testid="insurance-field-status"
            value={policyForm.status}
            onChange={(e) => setPolicyForm({ ...policyForm, status: (e.target as HTMLSelectElement).value as InsurancePolicyRow['status'] })}
          >
            {INSURANCE_STATUSES.map((s) => (
              <option key={s} value={s}>{INSURANCE_STATUS_LABEL[s]}</option>
            ))}
          </select>
        </label>
        <label>
          Notes
          <input
            data-testid="insurance-field-notes"
            value={policyForm.notes}
            onInput={(e) => setPolicyForm({ ...policyForm, notes: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label>
          Source
          <input
            data-testid="insurance-field-source"
            value={policyForm.source}
            onInput={(e) => setPolicyForm({ ...policyForm, source: (e.target as HTMLInputElement).value })}
          />
        </label>
        <div class="service-point-actions">
          <button type="submit" class="btn primary" disabled={save.busy || !policyForm.coverageType.trim()}>
            {policyForm.id ? 'Save' : 'Add'}
          </button>
          <button type="button" class="btn" disabled={save.busy} onClick={() => setPolicyForm(null)}>
            Cancel
          </button>
        </div>
      </form>
    );
  }

  function renderLocationsForm(): JSX.Element {
    if (!locationsForm) throw new Error('renderLocationsForm called with no form open');
    return (
      <form
        class="service-point-form"
        data-testid="insurance-locations-form"
        onSubmit={(e) => { e.preventDefault(); void submitLocations(); }}
      >
        <table data-testid="insurance-locations-editor">
          <thead>
            <tr><th>Clinic</th><th>Covered</th><th>Payroll basis</th><th>Note</th></tr>
          </thead>
          <tbody>
            {LOCATION_ORDER.map((code) => {
              const row = locationsForm.rows[code];
              return (
                <tr key={code}>
                  <td>{LOCATION_NAMES[code]}</td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Covers ${LOCATION_NAMES[code]}`}
                      data-testid={`insurance-location-checked-${code}`}
                      checked={row.checked}
                      onChange={(e) => setLocationsForm({
                        ...locationsForm,
                        rows: { ...locationsForm.rows, [code]: { ...row, checked: (e.target as HTMLInputElement).checked } },
                      })}
                    />
                  </td>
                  <td>
                    <input
                      data-testid={`insurance-location-payroll-${code}`}
                      value={row.payrollBasis}
                      disabled={!row.checked}
                      onInput={(e) => setLocationsForm({
                        ...locationsForm,
                        rows: { ...locationsForm.rows, [code]: { ...row, payrollBasis: (e.target as HTMLInputElement).value } },
                      })}
                    />
                  </td>
                  <td>
                    <input
                      data-testid={`insurance-location-note-${code}`}
                      value={row.note}
                      disabled={!row.checked}
                      onInput={(e) => setLocationsForm({
                        ...locationsForm,
                        rows: { ...locationsForm.rows, [code]: { ...row, note: (e.target as HTMLInputElement).value } },
                      })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div class="service-point-actions">
          <button type="submit" class="btn primary" disabled={save.busy}>Save</button>
          <button type="button" class="btn" disabled={save.busy} onClick={() => setLocationsForm(null)}>Cancel</button>
        </div>
      </form>
    );
  }

  /** One policy row's cells, shared between the current table and the past
   * table — `washed` is false for the past table (Tomer, 2026-09-26: "same
   * columns, no wash"). */
  function policyRow(policy: InsurancePolicyRow, washed: boolean): JSX.Element {
    const key = `policy-${policy.id}`;
    const docs = documentsForPolicy(documents, policy.id);
    const daysLeft = policy.expirationDate ? policyDaysLeft(policy.expirationDate, today) : null;
    const wash = washed ? expiryWash(daysLeft) : undefined;
    const docsOpen = Boolean(openDocs[key]);
    const confirming = confirmingPolicyId === policy.id;
    const locationNames = policyLocationNames(policy);
    const rows: (JSX.Element | null)[] = [
      (
        <tr key={key} data-testid={`insurance-row-${key}`} class={wash}>
          <td>
            <a
              class="candidate-link"
              href={`#insurance-${policy.id}`}
              data-testid={`insurance-open-${policy.id}`}
              onClick={(e: MouseEvent) => { e.preventDefault(); openProfile(policy.id); }}
            >
              {policy.coverageType}
            </a>
          </td>
          <td>{policy.carrier || EM_DASH}</td>
          <td>{policy.policyNumber || EM_DASH}</td>
          <td>{policy.namedInsured || EM_DASH}</td>
          <td>{locationNames.length ? locationNames.join(', ') : EM_DASH}</td>
          <td>{policy.effectiveDate ? isoToMd(policy.effectiveDate) : EM_DASH}</td>
          <td data-testid={`insurance-expires-${key}`}>
            {policy.expirationDate ? (
              <>
                {isoToMd(policy.expirationDate)}
                {washed && daysLeft !== null && daysLeft >= 0 && daysLeft <= INSURANCE_ATTENTION_DAYS ? (
                  <div class={daysLeft <= INSURANCE_WARN_DAYS ? 'muted warn' : 'muted'}>
                    {`in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
                  </div>
                ) : null}
              </>
            ) : EM_DASH}
          </td>
          <td>{policy.limits || EM_DASH}</td>
          <td>{policy.deductible || EM_DASH}</td>
          <td>
            <button
              type="button"
              class="btn"
              data-testid={`insurance-documents-toggle-${key}`}
              onClick={() => setOpenDocs((prev) => ({ ...prev, [key]: !prev[key] }))}
            >
              {`Documents (${docs.length})`}
            </button>
          </td>
          {canWrite ? (
            <td>
              {confirming ? (
                <>
                  <span>Remove this policy?</span>
                  <button type="button" class="btn" disabled={save.busy} onClick={() => void removePolicyConfirmed(policy.id)}>Confirm</button>
                  <button type="button" class="btn" disabled={save.busy} onClick={() => setConfirmingPolicyId(null)}>Cancel</button>
                </>
              ) : (
                <>
                  <button type="button" class="btn" disabled={save.busy} onClick={() => openPolicyForm(policyFormFrom(policy))}>Edit</button>
                  <button
                    type="button"
                    class="btn"
                    data-testid={`insurance-remove-${policy.id}`}
                    disabled={save.busy}
                    onClick={() => setConfirmingPolicyId(policy.id)}
                  >
                    Remove
                  </button>
                </>
              )}
            </td>
          ) : null}
        </tr>
      ),
      docsOpen ? (
        <tr key={`${key}-docs`} data-testid={`insurance-documents-panel-${key}`}>
          <td colSpan={canWrite ? 11 : 10}>
            {renderDocuments(docs, key, policy.id)}
          </td>
        </tr>
      ) : null,
    ];
    return <>{rows}</>;
  }

  if (profilePolicyId !== null) {
    const policy = policies.find((p) => p.id === profilePolicyId) ?? null;
    if (!policy) {
      return (
        <div class="card" data-testid="company-insurance">
          <div class="candidate-page-bar">
            <button type="button" class="btn" data-testid="insurance-profile-back" onClick={() => openProfile(null)}>Back to all policies</button>
          </div>
          <EmptyState label="That policy is gone or was never here." />
        </div>
      );
    }
    const key = `policy-${policy.id}`;
    const docs = documentsForPolicy(documents, policy.id);
    const renewal = latestRenewalDocument(documents, policy.id);
    const isPast = policy.status === 'EXPIRED' || policy.status === 'CANCELLED';
    const daysLeft = policy.expirationDate ? policyDaysLeft(policy.expirationDate, today) : null;
    const wash = expiryWash(isPast ? null : daysLeft);
    const confirming = confirmingPolicyId === policy.id;
    return (
      <div class="card" data-testid="company-insurance">
        <div class="candidate-page-bar">
          <button type="button" class="btn" data-testid="insurance-profile-back" onClick={() => openProfile(null)}>Back to all policies</button>
        </div>
        <section class="insurance-profile" data-testid={`insurance-profile-${key}`}>
          <div class="section-head">
            <div class="section-head-row">
              <h3>{policy.coverageType}</h3>
              <div class="section-head-actions">
                <span class="badge">{INSURANCE_STATUS_LABEL[policy.status]}</span>
                {canWrite ? (
                  confirming ? (
                    <>
                      <span>Remove this policy?</span>
                      <button type="button" class="btn" disabled={save.busy} onClick={() => void removePolicyConfirmed(policy.id)}>Confirm</button>
                      <button type="button" class="btn" disabled={save.busy} onClick={() => setConfirmingPolicyId(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button type="button" class="btn" disabled={save.busy} onClick={() => openPolicyForm(policyFormFrom(policy))}>Edit</button>
                      <button type="button" class="btn" disabled={save.busy} onClick={() => setConfirmingPolicyId(policy.id)}>Remove</button>
                    </>
                  )
                ) : null}
              </div>
            </div>
          </div>
          {policyForm?.id === policy.id ? renderPolicyForm() : null}
          <table data-testid={`insurance-facts-${key}`}>
            <tbody>
              <tr><th scope="row">Carrier</th><td>{policy.carrier || EM_DASH}</td></tr>
              <tr><th scope="row">Policy #</th><td>{policy.policyNumber || EM_DASH}</td></tr>
              <tr><th scope="row">Named insured</th><td>{policy.namedInsured || EM_DASH}</td></tr>
              <tr><th scope="row">Effective</th><td>{policy.effectiveDate ? isoToMd(policy.effectiveDate) : EM_DASH}</td></tr>
              <tr class={wash}>
                <th scope="row">Expires</th>
                <td>
                  {policy.expirationDate ? isoToMd(policy.expirationDate) : EM_DASH}
                  {!isPast && daysLeft !== null && daysLeft >= 0 && daysLeft <= INSURANCE_ATTENTION_DAYS
                    ? ` (in ${daysLeft} day${daysLeft === 1 ? '' : 's'})` : ''}
                </td>
              </tr>
              <tr><th scope="row">Limits</th><td>{policy.limits || EM_DASH}</td></tr>
              <tr><th scope="row">Deductible</th><td>{policy.deductible || EM_DASH}</td></tr>
              <tr><th scope="row">BPP</th><td>{policy.bpp || EM_DASH}</td></tr>
              <tr><th scope="row">Premium</th><td>{policy.premium != null ? USD.format(Number(policy.premium)) : EM_DASH}</td></tr>
              <tr><th scope="row">Broker</th><td>{policy.broker || EM_DASH}{policy.brokerContact ? ` (${policy.brokerContact})` : ''}</td></tr>
              <tr><th scope="row">Last renewal document</th><td>{renewal ? `${isoToMd(renewal.docDate)} — ${renewal.title}` : EM_DASH}</td></tr>
              <tr><th scope="row">Notes</th><td>{policy.notes || EM_DASH}</td></tr>
              <tr><th scope="row">Source</th><td>{policy.source || EM_DASH}</td></tr>
            </tbody>
          </table>
          <div class="section-head">
            <div class="section-head-row">
              <h3>Locations</h3>
              {canWrite && locationsForm?.policyId !== policy.id ? (
                <button type="button" class="btn" data-testid={`insurance-edit-locations-${policy.id}`} onClick={() => setLocationsForm(blankLocationsForm(policy))}>
                  Edit locations
                </button>
              ) : null}
            </div>
          </div>
          {locationsForm?.policyId === policy.id ? renderLocationsForm() : (
            policy.locations.length === 0 ? (
              <p class="empty">No clinics recorded yet.</p>
            ) : (
              <table data-testid={`insurance-locations-${policy.id}`}>
                <thead><tr><th>Clinic</th><th>Payroll basis</th><th>Note</th></tr></thead>
                <tbody>
                  {LOCATION_ORDER.filter((code) => policy.locations.some((l) => l.locationCode === code)).map((code) => {
                    const loc = policy.locations.find((l) => l.locationCode === code);
                    return (
                      <tr key={code}>
                        <td>{LOCATION_NAMES[code]}</td>
                        <td>{loc?.payrollBasis || EM_DASH}</td>
                        <td>{loc?.note || EM_DASH}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          )}
          <h3>{`Documents (${docs.length})`}</h3>
          {renderDocuments(docs, key, policy.id)}
        </section>
      </div>
    );
  }

  return (
    <div class="card" data-testid="company-insurance">
      <div class="section-head">
        <div class="section-head-row">
          <h2>Insurance</h2>
          <div class="section-head-actions">
            {canWrite ? (
              <button type="button" class="btn primary" data-testid="add-insurance-policy" onClick={() => openPolicyForm(blankPolicyForm())}>
                Add policy
              </button>
            ) : null}
            {pinKey ? <FavoriteStar itemKey={pinKey} /> : null}
          </div>
        </div>
        <p>Every policy on file, and the paperwork filed against it, oldest to newest.</p>
        {save.error ? <p class="bad" role="alert">{save.error}</p> : null}
      </div>

      {policyForm ? renderPolicyForm() : null}

      <div class="table-wrap">
        <table class="data" data-testid="insurance-table">
          <thead>
            <tr>
              <th>Coverage</th>
              <th>Carrier</th>
              <th>Policy #</th>
              <th>Insured</th>
              <th>Locations</th>
              <th>Effective</th>
              <th>Expires</th>
              <th>Limits</th>
              <th>Deductible</th>
              <th>Documents</th>
              {canWrite ? <th></th> : null}
            </tr>
          </thead>
          <tbody>
            {current.length === 0 ? (
              <tr><td colSpan={canWrite ? 11 : 10} class="empty">No policies on file.</td></tr>
            ) : current.map((policy) => policyRow(policy, true))}
          </tbody>
        </table>
      </div>

      {past.length > 0 ? (
        <div class="section-head">
          <div class="section-head-row">
            <button
              type="button"
              class="btn"
              aria-pressed={showPast}
              data-testid="insurance-show-past"
              onClick={() => setShowPast((v) => !v)}
            >
              {showPast ? `Hide past policies (${past.length})` : `Show past policies (${past.length})`}
            </button>
          </div>
        </div>
      ) : null}
      {showPast && past.length > 0 ? (
        <div class="table-wrap">
          <table class="data" data-testid="insurance-past-table">
            <thead>
              <tr>
                <th>Coverage</th>
                <th>Carrier</th>
                <th>Policy #</th>
                <th>Insured</th>
                <th>Locations</th>
                <th>Effective</th>
                <th>Expires</th>
                <th>Limits</th>
                <th>Deductible</th>
                <th>Documents</th>
                {canWrite ? <th></th> : null}
              </tr>
            </thead>
            <tbody>
              {past.map((policy) => policyRow(policy, false))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div class="section-head">
        <div class="section-head-row">
          <h3>Certificates &amp; other documents</h3>
        </div>
        <p>Filed to a clinic with no single policy, or company-wide across several policies at once.</p>
      </div>
      {renderDocuments(unattached, 'unattached', null)}
    </div>
  );
}
